/* eslint-disable react-refresh/only-export-components -- entry file:
   App's root tree lives here intentionally; we don't HMR the router. */
import { lazy, Suspense } from "react";

// === Stale-chunk recovery ==================================================
//
// Vite emits chunks with content-hashed filenames (CoherenceModal-CAfnDTGq.js).
// When we ship a new build, CloudFront caches the new ``index-<hash>.html``
// AND the new ``index-<hash>.js`` AND the new chunk hashes. But a user who
// already had the OLD ``index-<hash>.js`` cached in their browser is still
// asking for the OLD ``CoherenceModal-<oldhash>.js`` — which CloudFront
// no longer has, because we replaced it in the deploy.
//
// Result: ``error loading dynamically imported module:
// https://…/assets/CoherenceModal-CAfnDTGq.js``. The user is stranded
// until they hard-refresh.
//
// Two safety nets:
//
// 1. Listen for ``vite:preloadError`` (Vite's first-class signal for
//    chunk-import failures) and trigger one reload. ``e.preventDefault()``
//    suppresses the default reload-without-cache so we control timing.
//
// 2. Wrap every ``lazy()`` call below with ``lazyWithRetry`` so even if
//    the global listener misses (e.g. an in-flight chunk request the
//    browser routes through the network stack before our handler
//    registers), the per-route loader catches the rejection and reloads.
//
// Both gates a single retry per session via ``sessionStorage`` so a
// genuine server outage doesn't reload-loop the user.
const RETRY_KEY = "wo:chunk-retry";
function shouldRetry() {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(RETRY_KEY) !== "1";
  } catch {
    return true; // private mode → allow one reload attempt
  }
}
function markRetried() {
  try {
    window.sessionStorage.setItem(RETRY_KEY, "1");
  } catch {
    /* silent */
  }
}
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (e) => {
    if (shouldRetry()) {
      markRetried();
      e.preventDefault?.();
      window.location.reload();
    }
  });
}

function lazyWithRetry(loader) {
  return lazy(async () => {
    try {
      return await loader();
    } catch (err) {
      // Network / chunk-not-found typically surfaces as TypeError
      // "Failed to fetch dynamically imported module" or an Error
      // whose message contains "dynamically imported module".
      const msg = String(err?.message || err || "");
      const isChunkErr =
        msg.includes("dynamically imported") ||
        msg.includes("Failed to fetch") ||
        msg.includes("Loading chunk");
      if (isChunkErr && shouldRetry()) {
        markRetried();
        window.location.reload();
        // Return an unresolved promise to keep Suspense paused
        // until the reload actually kicks in. Re-throwing would
        // flash the ErrorBoundary for a frame.
        return new Promise(() => {});
      }
      throw err;
    }
  });
}
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import "./index.css";
import "react-toastify/dist/ReactToastify.css";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import ErrorBoundary from "./components/Shared/ErrorBoundary.jsx";
import Footer from "./components/Shared/Footer.jsx";
import ConfigErrorScreen from "./components/Shared/ConfigErrorScreen.jsx";
import NotFound from "./components/Shared/NotFound.jsx";
import ErrorPage from "./components/Shared/ErrorPage.jsx";
import { ThemeProvider } from "./theme/ThemeContext.jsx";
import { I18nProvider } from "./i18n/I18nContext.jsx";
import { AuthProvider } from "./auth/AuthContext.jsx";
import { PersonalNarrationProvider } from "./state/personalNarrationContext.jsx";
import PersonalNarrationBackground from "./components/Reader/PersonalNarrationBackground.jsx";
import RequireAuth from "./auth/RequireAuth.jsx";
import RequireGuest from "./auth/RequireGuest.jsx";
import SkipLink from "./a11y/SkipLink.jsx";
import { ReadingPrefsProvider } from "./a11y/ReadingPrefsContext.jsx";
import { validateConfig } from "./util/config.js";
// Eager — these are the public-landing critical path and small
// auth pages. Keeping them out of the lazy bucket means a first
// paint on /, /login, /register doesn't pay a Suspense round-trip.
import UserHome from "./components/UserHome/UserHome.jsx";
import EditorAccess from "./components/WriteProject/EditorAccess.jsx";
import Login from "./components/Login/Login.jsx";
import SignUp from "./components/Login/SignUp.jsx";
import ResetPasswordPage from "./components/Login/ResetPasswordPage.jsx";

// Lazy — every non-critical-path route is split into its own
// chunk. The headline win is keeping the editor's huge
// dependency tree (TipTap + Yjs + Hocuspocus + ChatAi) out of
// the public bundle: a visitor landing on /browse no longer
// downloads the writing tooling. Each ``lazy()`` returns a
// promise the Suspense boundary below resolves; the fallback
// renders for a beat on first navigation, then the chunk caches.
const BookView = lazyWithRetry(() => import("./components/BookView/BookView.jsx"));
const WritingScreen = lazyWithRetry(() => import("./components/WritingScreen/WritingScreen.jsx"));
const EditProject = lazyWithRetry(() => import("./components/EditProject/EditProject.jsx"));
const CreateProject = lazyWithRetry(() => import("./components/CreateProject/CreateProject.jsx"));
const Goal = lazyWithRetry(() => import("./components/Goal/Goal.jsx"));
const WritingGoals = lazyWithRetry(() => import("./components/Goal/WritingGoals.jsx"));
const Profile = lazyWithRetry(() => import("./components/Profile/Profile.jsx"));
const Collaboration = lazyWithRetry(() => import("./components/Collaboration/Collaboration.jsx"));
const Help = lazyWithRetry(() => import("./components/Help/Help.jsx"));
const Contact = lazyWithRetry(() => import("./components/Contact/Contact.jsx"));
const Search = lazyWithRetry(() => import("./components/Search/Search.jsx"));
const SearchResult = lazyWithRetry(() => import("./components/Search/SearchResult.jsx"));
const Settings = lazyWithRetry(() => import("./components/Settings/Settings.jsx"));
const Terms = lazyWithRetry(() => import("./components/Login/Terms.jsx"));
const Messages = lazyWithRetry(() => import("./components/Message/Messages.jsx"));
const Games = lazyWithRetry(() => import("./components/Games/Games.jsx"));
const Curiosities = lazyWithRetry(() =>
  import("./components/Curiosities/Curiosities.jsx"),
);


// Route table. Three tiers:
//   * Public       — landing, browse, book reader, forum, help, terms, 404.
//   * Guest-only   — /login, /register (signed-in users bounce to ?next or /).
//   * Auth-only    — everything else (tripped to /login?next=<original>).
const guarded = (element) => <RequireAuth>{element}</RequireAuth>;
const guestOnly = (element) => <RequireGuest>{element}</RequireGuest>;

// ``Lazy`` wraps a lazy-loaded element in the standard Suspense
// fallback. Defined once so every route renders the same brief
// loading state instead of inventing a per-route placeholder.
// The fallback is intentionally minimal — a full-page spinner
// would feel heavier than the chunk download itself on a warm
// cache.
function RouteFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-bg text-secondText text-sm"
      role="status"
      aria-live="polite"
    >
      <span className="opacity-60">Loading…</span>
    </div>
  );
}

const Lazy = (element) => (
  <Suspense fallback={<RouteFallback />}>{element}</Suspense>
);

// Every route gets the same ``errorElement`` so an uncaught render
// or loader error anywhere in the tree shows our themed page
// instead of React Router's default. ``/error`` is also a
// directly-navigable path for code that wants to bail out
// (e.g., a fatal API failure → ``navigate("/error")``).
const errorRoute = <ErrorPage />;
const withError = (routes) =>
  routes.map((r) => ({ ...r, errorElement: errorRoute }));

// Root layout — wraps every page. ``min-h-screen + flex-col`` plus the
// Footer's ``mt-auto`` mean short pages still push the footer to the
// bottom of the viewport instead of leaving it floating mid-screen.
// Pages that *should not* show the global footer (the writing editor's
// fullscreen experience, the focus mode) opt out by mounting their own
// ``hide-global-footer`` body class — see Footer.jsx if/when needed.
function RootLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Outlet />
      <Footer />
    </div>
  );
}

// AuthLayout — same min-h-screen flex column but **no global Footer**.
// Login / Sign-up / Reset password are tight, single-purpose pages
// that look cleaner without the footer's link/copyright row eating
// vertical space, especially on mobile where the auth form already
// pushes near the fold. Each page renders its own ``LocalePicker``
// pinned in the corner so the language switch is still reachable
// without Settings.
function AuthLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Outlet />
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    errorElement: errorRoute,
    children: withError([
      { path: "login", element: guestOnly(<Login />) },
      { path: "register", element: guestOnly(<SignUp />) },
      // Public — anyone with the token from their inbox can land
      // here. Don't gate on auth state: a signed-in user can still
      // reset their password from this page (the request can come
      // from PasswordForm in Settings or the Login → Forgot link),
      // and a logged-out user got here from the email link.
      { path: "reset-password", element: <ResetPasswordPage /> },
    ]),
  },
  {
    element: <RootLayout />,
    errorElement: errorRoute,
    children: withError([
      { path: "/", element: <App /> },
      { path: "error", element: <ErrorPage /> },
      { path: "browse", element: <UserHome /> },
      { path: "read/*", element: Lazy(<BookView />) },
      { path: "help", element: Lazy(<Help />) },
      { path: "contact", element: Lazy(<Contact />) },
      { path: "games", element: Lazy(<Games />) },
      { path: "curiosities", element: Lazy(<Curiosities />) },
      { path: "forum/*", element: Lazy(<SearchResult />) },
      { path: "profile/*", element: Lazy(<Profile />) },
      { path: "search", element: Lazy(<Search />) },
      { path: "terms", element: Lazy(<Terms />) },

      { path: "work", element: guarded(Lazy(<WritingScreen />)) },
      { path: "edit-project", element: guarded(Lazy(<EditProject />)) },
      { path: "project", element: guarded(Lazy(<CreateProject />)) },
      // ``EditorAccess`` is the gate: signed-in writers fall through to
      // ``WriteProject`` as before; anonymous visitors get a tailored
      // "log in to edit / open the reader" landing instead of a hard
      // redirect, so the editor URL is share-friendly even when the
      // recipient doesn't have an account. EditorAccess itself stays
      // eager (small) but the WriteProject it conditionally renders
      // is dynamically imported by the gate.
      { path: "editor/*", element: <EditorAccess /> },
      { path: "goals", element: guarded(Lazy(<Goal />)) },
      { path: "writing-goals", element: guarded(Lazy(<WritingGoals />)) },
      { path: "collaboration", element: guarded(Lazy(<Collaboration />)) },
      { path: "setting", element: guarded(Lazy(<Settings />)) },
      { path: "message/*", element: guarded(Lazy(<Messages />)) },

      { path: "*", element: <NotFound /> },
    ]),
  },
]);

const configStatus = validateConfig();
const appTree = configStatus.ok ? (
  // HelmetProvider must wrap the i18n + router so per-page <Seo>
  // components can both read the active locale and update <head>.
  <HelmetProvider>
    <I18nProvider>
      <ThemeProvider>
        <ReadingPrefsProvider>
        <AuthProvider>
          <PersonalNarrationProvider>
            <ErrorBoundary>
              <SkipLink />
              <RouterProvider router={router} />
              {/* Floating "Generating your narration…" badge + a
                  react-toastify call when the bg-job finishes.
                  Lives OUTSIDE the router so the badge survives
                  every navigation -- writers can leave the reader
                  while F5-TTS works (8-15 min cold-start) and get
                  the toast when it lands. Reads from
                  PersonalNarrationProvider above. */}
              <PersonalNarrationBackground />
              {/* role="alert" on the toast region so screen readers
                  announce notifications as they arrive, instead of the
                  user having to discover them visually. */}
              <ToastContainer
                position="top-right"
                autoClose={4000}
                newestOnTop
                pauseOnHover
                theme="colored"
                role="alert"
              />
            </ErrorBoundary>
          </PersonalNarrationProvider>
        </AuthProvider>
        </ReadingPrefsProvider>
      </ThemeProvider>
    </I18nProvider>
  </HelmetProvider>
) : (
  <ThemeProvider>
    <ConfigErrorScreen missing={configStatus.missing} />
  </ThemeProvider>
);

ReactDOM.createRoot(document.getElementById("root")).render(appTree);
